import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, UserRound, X } from "lucide-react";
import { useStore } from "../store";
import { api, getImageUrl } from "../api";

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const { cartCount, categories, ordersUpdated, customer, customerToken, logoutCustomer } = useStore();

  const deleteAccount = async () => {
    if (!window.confirm("Eliminare definitivamente il tuo account? I tuoi ordini resteranno, ma perderai il profilo.")) return;
    try {
      const { default: axios } = await import("axios");
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/auth/customer/me`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      logoutCustomer();
      setDrawerOpen(false);
      window.location.href = "/";
    } catch {
      alert("Eliminazione non riuscita. Riprova.");
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (!searchOpen || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.get("/products", { params: { search: query.trim() } })
        .then((r) => setSuggestions(r.data.products.slice(0, 5)))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, searchOpen]);

  const goSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchOpen(false);
      setSuggestions([]);
      navigate(`/cerca?q=${encodeURIComponent(query.trim())}`);
      setQuery("");
    }
  };

  const goToProduct = (id) => {
    setSearchOpen(false);
    setSuggestions([]);
    setQuery("");
    navigate(`/prodotto/${id}`);
  };

  return (
    <>
      {/* Gold announcement bar */}
      <div className="gold-gradient text-white text-center py-2 text-[11px] font-semibold tracking-wide-spaced uppercase" data-testid="announcement-bar">
        Spedizione gratuita da €99
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#EAE4DA]" data-testid="header">
        <div className="relative flex items-center justify-between h-16 px-4">
          <button
            data-testid="menu-button"
            aria-label="Apri menu"
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-2 text-[#2C2C2C] hover:opacity-60 transition-opacity duration-200"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>

          <Link to="/" data-testid="logo-link" className="absolute left-1/2 -translate-x-1/2">
            <img src="/brand/logo-content.png" alt="Dorae" className="h-11 object-contain" />
          </Link>

          <div className="flex items-center gap-1">
            <button
              data-testid="search-button"
              aria-label="Cerca"
              onClick={() => setSearchOpen(true)}
              className="p-2 text-[#2C2C2C] hover:opacity-60 transition-opacity duration-200"
            >
              <Search size={20} strokeWidth={1.5} />
            </button>
            <Link to="/carrello" data-testid="cart-button" aria-label="Carrello" className="p-2 -mr-2 text-[#2C2C2C] relative hover:opacity-60 transition-opacity duration-200">
              <ShoppingBag size={20} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[#D4AF37] text-white text-[10px] font-semibold min-w-[17px] h-[17px] rounded-full flex items-center justify-center px-0.5">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* Drawer overlay */}
      <div
        className={`drawer-overlay fixed inset-0 bg-black/40 z-50 ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setDrawerOpen(false)}
        data-testid="drawer-overlay"
      />

      {/* Drawer panel */}
      <aside
        className={`drawer-panel fixed top-0 left-0 h-full w-[82%] max-w-sm bg-[#FAF7F2] z-50 flex flex-col ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="drawer"
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-[#EAE4DA]">
          <img src="/brand/logo-content.png" alt="Dorae" className="h-9 object-contain" />
          <button data-testid="drawer-close" aria-label="Chiudi menu" onClick={() => setDrawerOpen(false)} className="p-2 -mr-2 text-[#2C2C2C]">
            <X size={22} strokeWidth={1.5} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar py-2" data-testid="drawer-nav">
          {customer ? (
            <div className="flex items-center gap-3 px-6 py-4 border-b border-[#EDE6DB]" data-testid="drawer-account">
              <span className="w-9 h-9 rounded-full gold-gradient flex items-center justify-center shrink-0">
                <UserRound size={18} strokeWidth={1.8} className="text-white" />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold tracking-wide text-[#2C2C2C] truncate" data-testid="drawer-account-name">
                  {customer.name || "Il tuo account"}
                </p>
                <p className="text-[11px] text-[#8B8578] truncate">{customer.email}</p>
              </div>
            </div>
          ) : (
            <DrawerLink to="/benvenuto" label="Registrati" onClick={() => setDrawerOpen(false)} testid="nav-registrati" highlight />
          )}
          <DrawerLink to="/" label="Home" onClick={() => setDrawerOpen(false)} testid="nav-home" />
          <Link to="/i-miei-ordini" onClick={() => setDrawerOpen(false)} data-testid="nav-i-miei-ordini" className="flex items-center justify-between px-6 py-4 text-[13px] tracking-wide-spaced uppercase border-b border-[#EDE6DB] text-[#2C2C2C] transition-colors duration-200 hover:bg-[#F3EDE3] hover:text-[#AA7C11]">
            <span>I miei ordini</span>
            {ordersUpdated > 0 && (
              <span data-testid="orders-updated-badge" className="bg-[#D4AF37] text-white text-[10px] font-semibold min-w-[17px] h-[17px] rounded-full flex items-center justify-center px-1">
                {ordersUpdated}
              </span>
            )}
          </Link>
          <div className="gold-line mx-6 my-1" />
          {categories.map((c) => (
            <DrawerLink key={c.slug} to={`/categoria/${c.slug}`} label={c.name} onClick={() => setDrawerOpen(false)} testid={`nav-${c.slug}`} />
          ))}
          {customer && (
            <>
              <div className="gold-line mx-6 my-1" />
              <button data-testid="nav-logout" onClick={() => { logoutCustomer(); setDrawerOpen(false); }} className="block w-full text-left px-6 py-4 text-[13px] tracking-wide-spaced uppercase border-b border-[#EDE6DB] text-[#2C2C2C] transition-colors duration-200 hover:bg-[#F3EDE3] hover:text-[#AA7C11]">
                Logout
              </button>
              <button data-testid="nav-delete-account" onClick={deleteAccount} className="block w-full text-left px-6 py-4 text-[13px] tracking-wide-spaced uppercase border-b border-[#EDE6DB] text-red-600 transition-colors duration-200 hover:bg-[#F9EFEF]">
                Elimina account
              </button>
            </>
          )}
        </nav>
        <div className="px-6 py-6 text-[11px] tracking-wide-spaced uppercase text-[#8B5A2B] fabric-texture">
          Dorae · Abbigliamento femminile
        </div>
      </aside>

      {/* Search overlay */}
      <div
        className={`drawer-overlay fixed inset-0 bg-black/40 z-50 ${searchOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setSearchOpen(false)}
      />
      <div className={`drawer-panel fixed top-0 left-0 right-0 bg-white z-50 ${searchOpen ? "translate-y-0" : "-translate-y-full"}`} data-testid="search-overlay">
        <form onSubmit={goSearch} className="flex items-center gap-3 px-4 h-16 border-b border-[#EAE4DA]">
          <Search size={20} strokeWidth={1.5} className="text-[#8B5A2B] shrink-0" />
          <input
            data-testid="search-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca un capo... es. gonna, vestito, jeans"
            className="flex-1 bg-transparent outline-none text-base tracking-wide placeholder:text-[#B0A898]"
          />
          <button type="button" data-testid="search-close" aria-label="Chiudi ricerca" onClick={() => setSearchOpen(false)} className="p-2 -mr-2">
            <X size={20} strokeWidth={1.5} />
          </button>
        </form>
        {suggestions.length > 0 && (
          <ul className="max-h-[60vh] overflow-y-auto" data-testid="search-suggestions">
            {suggestions.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  data-testid={`suggestion-${p.id}`}
                  onClick={() => goToProduct(p.id)}
                  className="w-full flex items-center gap-4 px-4 py-3 border-b border-[#F0EBE2] text-left hover:bg-[#FAF7F2] transition-colors"
                >
                  <img src={getImageUrl(p.image_url)} alt={p.name} className="w-11 h-14 object-cover bg-white" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm text-[#2C2C2C] truncate">{p.name}</p>
                    <p className="text-xs font-semibold gold-gradient-text mt-0.5">€ {p.price.toFixed(2)}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {searchOpen && query.trim().length >= 2 && suggestions.length === 0 && (
          <p className="px-4 py-5 text-xs tracking-wide text-[#8B8578]" data-testid="search-no-results">
            Nessun risultato per “{query.trim()}”.
          </p>
        )}
      </div>
    </>
  );
}

function DrawerLink({ to, label, onClick, testid, subtle, highlight }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testid}
      className={`block px-6 py-4 text-[13px] tracking-wide-spaced uppercase border-b border-[#EDE6DB] transition-colors duration-200 hover:bg-[#F3EDE3] ${highlight ? "font-semibold gold-gradient-text" : subtle ? "text-[#8B5A2B]" : "text-[#2C2C2C] hover:text-[#AA7C11]"}`}
    >
      {label}
    </Link>
  );
}
